import { yupResolver } from '@hookform/resolvers/yup';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import {
  AccountType,
  patchV1CompaniesCompanyIdJobsId,
  RateOwnerType,
} from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';

import Dialog from '~components/Dialog/Dialog';
import DialogActions from '~components/Dialog/DialogActions';
import DialogContent from '~components/Dialog/DialogContent';
import DialogHeader from '~components/Dialog/DialogHeader';
import { AutocompleteFormField } from '~components/FormFields/AutocompleteFormField';
import {
  OverflowAwareText,
  SmallButton,
  SmallLoadingButton,
} from '~components/Order/ordersDispatchStyledComponents';
import { payCommissionFieldError } from '~components/PayCommission/payCommissionFieldError';
import PricingHistoryButton from '~components/PricingHistory/PricingHistoryButton';
import { FeatureFlags } from '~constants/featureFlags';
import { Job } from '~hooks/useJob/models';
import { SiteRateSchedule } from '~hooks/useSiteRateSchedule/models';
import { AddOnAssignmentSchema } from '~pages/Sales/Orders/NewOrderForm/components/AutomaticAddOns';
import {
  deparseUpdateRateDetailsDTO,
  RateDetailsDTO,
} from '~pages/Sales/Orders/NewOrderForm/schema';
import useRateDetails from '~pages/Sales/Orders/NewOrderForm/useRateDetails';
import { deriveDriverRateFromVendorRate } from '~pages/Sales/Orders/NewOrderForm/utils';
import {
  getHaulerRateTypeOptions,
  getRateTypeLabel,
} from '~pages/Settings/RatesManagement/rateUtils';
import connection from '~services/connectionModule';
import { useStores } from '~store';
import { alert, AlertTypes } from '~types/AlertTypes';
import { useFeatureFlag } from '~utils/hooks/useFeatureFlag';
import useShouldShowHaulerRateCard from '~utils/hooks/useShouldShowHaulerRateCard';
import {
  extractRateInfoFromJob,
  jobPayCommission,
  payCommissionPatch,
  resolveJobRateSides,
  resolveRateDisabledReasons,
  resolveRateEditableSides,
} from '~utils/jobs/job-utils';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

import useJobScheduleRates, { JobRateInputHandler } from '../hooks/useJobScheduleRates';
import { fetchParentAddOnAssignmentsForJob } from '../utils/fetchParentAddOnAssignmentsForJob';
import { processAddOnAssignmentsForJobs } from '../utils/processAddOnAssignmentsForJobs';
import JobFreightRateRow from './JobFreightRateRow';

const haulerFormSchema = yup.object().shape({
  haulerRateType: yup
    .object()
    .shape({ label: yup.string().required(), value: yup.string().required() })
    .notRequired(),
});
type HaulerFormDTO = yup.InferType<typeof haulerFormSchema>;

// Mirrors makeCustomerSpecificJobFormSchema (JobFormSchema.ts): the hauler rate
// type is only required under the Tomlinson flag; under the generic
// show_hauler_rate_type_card flag it is optional and clearable.
function makeHaulerFormSchema(tomlinsonOrderModalFeatureFlagEnabled: boolean) {
  if (tomlinsonOrderModalFeatureFlagEnabled) {
    return haulerFormSchema.shape({
      haulerRateType: yup
        .object()
        .shape({ label: yup.string().required(), value: yup.string().required() })
        .required(),
    });
  }

  return haulerFormSchema;
}

interface EditJobRatesDialogProps {
  isOrderOwner?: boolean;
  job?: Job;
  onClose: () => void;
}

export default function EditJobRatesDialog({
  isOrderOwner,
  job,
  onClose,
}: EditJobRatesDialogProps) {
  const isOpen = Boolean(job);

  const { ordersDispatchStore, toasterStore, userStore } = useStores();
  const companyId = getEffectiveUserCompanyId(userStore);

  const defaultVendorRateToDriverFeatureFlagEnabled = useFeatureFlag({
    featureFlagKey: FeatureFlags.defaultVendorRateToDriver,
  });
  const shouldShowHaulerRateCard = useShouldShowHaulerRateCard();
  // Mirrors JobEditForm: the hauler rate type is only a required field under the
  // Tomlinson flag; for the generic show_hauler_rate_type_card flag it is optional.
  const tomlinsonOrderModalFeatureFlagEnabled = useFeatureFlag({
    featureFlagKey: FeatureFlags.tomlinsonOrderModal,
  });

  const initialRates = useMemo(() => {
    if (!job) {
      return { hauler: null, customer: null, vendor: null, driver: null };
    }

    const { hasCustomerRateSide, hasVendorRateSide, isAssignedToDriver } =
      resolveJobRateSides(job);

    const customerRate = extractRateInfoFromJob(job, 'customer');
    const customer = hasCustomerRateSide
      ? {
          // Preserve the matched rate card whenever one is present (mirrors
          // JobEditForm.getDefaultRate). Gating on `rate.rate > 0` dropped the
          // card object — and its name — for $0 and flat-commission cards,
          // blanking the name on the Edit Rate form (TRE-15390).
          rate: job.customerJobAssignment?.rate ?? null,
          rateType: customerRate?.rateType ?? null,
          rateValue: customerRate?.rateValue ?? null,
        }
      : null;

    const vendorRate = extractRateInfoFromJob(job, 'pay');
    const vendor = hasVendorRateSide
      ? {
          commission: jobPayCommission(job, 'vendor'),
          rate: job.vendorJobAssignment?.rate ?? null,
          rateType: vendorRate?.rateType ?? null,
          rateValue: vendorRate?.rateValue ?? null,
        }
      : null;

    const driverRate = extractRateInfoFromJob(job, 'driver');
    const driver = isAssignedToDriver
      ? {
          commission: jobPayCommission(job, 'driver'),
          rate: job.driverRate ?? null,
          rateType: driverRate?.rateType ?? null,
          rateValue: driverRate?.rateValue ?? null,
        }
      : null;

    const hauler = shouldShowHaulerRateCard
      ? { rate: null, rateType: job.haulerRateType ?? null, rateValue: 0 }
      : null;

    return { hauler, customer, vendor, driver };
  }, [job, shouldShowHaulerRateCard]);

  const hasHaulerRate = Boolean(initialRates.hauler);
  const haulerFormResolverSchema = useMemo(
    () => makeHaulerFormSchema(tomlinsonOrderModalFeatureFlagEnabled),
    [tomlinsonOrderModalFeatureFlagEnabled],
  );
  const haulerForm = useForm<HaulerFormDTO>({
    resolver: yupResolver(haulerFormResolverSchema),
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: {
      haulerRateType: initialRates.hauler?.rateType
        ? {
            label: getRateTypeLabel(initialRates.hauler.rateType),
            value: initialRates.hauler.rateType,
          }
        : null,
    },
  });

  const [isSaving, setIsSaving] = useState(false);

  const canEditVendorRate = Boolean(initialRates.vendor);
  const canEditDriverRate = Boolean(initialRates.driver);
  const canDefaultPayRateToDriver =
    defaultVendorRateToDriverFeatureFlagEnabled && canEditVendorRate && canEditDriverRate;

  const [isDefaultingPayRateToDriver] = useState(
    canDefaultPayRateToDriver ? !initialRates.driver?.rateType : false,
  );

  const customerJobRateInputRef = useRef<JobRateInputHandler>(null);
  const vendorOrDriverJobRateInputRef = useRef<JobRateInputHandler>(null);

  const { isAssignedToDriver, isAssignedToVendor } = job
    ? resolveJobRateSides(job)
    : { isAssignedToDriver: false, isAssignedToVendor: false };

  // The API refuses a rate change to a side whose invoice is approved, and
  // rejects the whole payload with it. No job means the dialog is closed.
  const rateEditable = job
    ? resolveRateEditableSides(job, isAssignedToVendor)
    : { customer: true, assignee: true };
  const rateDisabledReasons = resolveRateDisabledReasons(
    rateEditable,
    Boolean(isOrderOwner),
  );

  const [schedule, setSchedule] = useState<SiteRateSchedule | null>(
    job?.siteRateSchedule ?? null,
  );

  // When the dialog reopens with a different job, reset the local schedule
  // state to that job's stamp. Without this, switching jobs leaves the
  // previous job's schedule selected.
  useEffect(() => {
    setSchedule(job?.siteRateSchedule ?? null);
  }, [job?.id]);

  const {
    applySchedule,
    clearSchedule,
    unlinkSchedule,
    onCustomerRateFieldsChange,
    onAssigneeRateFieldsChange,
  } = useJobScheduleRates({
    getSchedule: () => schedule,
    setSchedule,
    isAssignedToVendor,
    customerRateDetailsRef: customerJobRateInputRef,
    vendorOrDriverRateDetailsRef: vendorOrDriverJobRateInputRef,
  });

  const customerAccountId = job?.customerJobAssignment?.customerAccount?.id;
  const vendorAccountId = job?.vendorJobAssignment?.vendorAccount?.id;
  const projectId = job?.project?.id;
  const serviceId = job?.service?.id;
  const driverId = job?.driver?.id;

  const { fetchCustomerRates } = useRateDetails({
    accountId: customerAccountId,
    companyId,
    projectId,
    serviceId,
  });
  const { fetchVendorRates } = useRateDetails({
    accountId: vendorAccountId,
    companyId,
    projectId,
    serviceId,
  });
  const { fetchDriverRates } = useRateDetails({
    driverId,
    companyId,
    projectId,
    serviceId,
  });

  const updateJobRates = useCallback(
    async ({
      customerRate,
      driverRate,
      haulerRate,
      vendorRate,
      customerAddOnAssignments,
      vendorAddOnAssignments,
      driverAddOnAssignments,
      customerAddOnsDidChange,
      vendorAddOnsDidChange,
      driverAddOnsDidChange,
      siteRateScheduleId,
    }: {
      customerRate: RateDetailsDTO | undefined;
      driverRate: RateDetailsDTO | undefined;
      haulerRate: string | undefined;
      vendorRate: RateDetailsDTO | undefined;
      customerAddOnAssignments: AddOnAssignmentSchema[];
      vendorAddOnAssignments: AddOnAssignmentSchema[];
      driverAddOnAssignments: AddOnAssignmentSchema[];
      customerAddOnsDidChange: boolean;
      vendorAddOnsDidChange: boolean;
      driverAddOnsDidChange: boolean;
      siteRateScheduleId: string | null | undefined;
    }) => {
      const scheduleChanged =
        siteRateScheduleId !== undefined &&
        siteRateScheduleId !== (job?.siteRateSchedule?.id ?? null);
      const hasRates = Boolean(
        customerRate || vendorRate || driverRate || haulerRate || scheduleChanged,
      );
      // On the edit path, the rate components signal "rate unchanged" by
      // passing `data: undefined` (preserves the SRS cascade — TRE-14094).
      // Without checking add-on changes, an add-on-only edit (add/edit/delete)
      // would gate out of submit entirely and silently drop the user's edits.
      const addOnsDidChange =
        customerAddOnsDidChange || vendorAddOnsDidChange || driverAddOnsDidChange;

      if (!job?.id || (!hasRates && !addOnsDidChange)) {
        return;
      }

      const deparsedCustomerRate = customerRate
        ? deparseUpdateRateDetailsDTO('customer', customerRate)
        : undefined;
      const deparsedVendorRate = vendorRate
        ? deparseUpdateRateDetailsDTO('vendor', vendorRate)
        : undefined;
      const deparsedDriverRate = driverRate
        ? deparseUpdateRateDetailsDTO('driver', driverRate)
        : undefined;

      const jobPatchBody = {
        ...deparsedCustomerRate,
        ...deparsedVendorRate,
        ...deparsedDriverRate,
        ...payCommissionPatch('vendor', vendorRate?.targetPayCommissionPercentage),
        ...payCommissionPatch('driver', driverRate?.targetPayCommissionPercentage),
        ...(haulerRate !== undefined ? { hauler_rate_type: haulerRate } : {}),
        ...(scheduleChanged ? { site_rate_schedule_id: siteRateScheduleId } : {}),
      };
      const hasJobPatchBody = Object.keys(jobPatchBody).length > 0;

      // Only sync a side's add-ons when that side actually changed. Without
      // the per-side gate, an addon-only edit on the customer side would also
      // re-PATCH every untouched driver/vendor add-on row.
      const addOnAssignmentsPromises = [
        vendorAddOnsDidChange && vendorAddOnAssignments.length > 0
          ? processAddOnAssignmentsForJobs({
              companyId,
              id: job?.vendorJobAssignment?.id ?? '',
              addOnAssignments: vendorAddOnAssignments,
              accountType: AccountType.VENDOR,
            })
          : Promise.resolve(),
        customerAddOnsDidChange && customerAddOnAssignments.length > 0
          ? processAddOnAssignmentsForJobs({
              companyId,
              id: job?.customerJobAssignment?.id ?? '',
              addOnAssignments: customerAddOnAssignments,
              accountType: AccountType.CUSTOMER,
            })
          : Promise.resolve(),
        driverAddOnsDidChange && driverAddOnAssignments.length > 0
          ? processAddOnAssignmentsForJobs({
              companyId,
              id: job?.id ?? '',
              addOnAssignments: driverAddOnAssignments,
            })
          : Promise.resolve(),
      ];

      try {
        setIsSaving(true);

        if (hasJobPatchBody) {
          const response = await patchV1CompaniesCompanyIdJobsId({
            path: { 'company-id': companyId, id: job.id },
            body: jobPatchBody,
          });

          const updatedJob = Job.parse(response.data.data);
          ordersDispatchStore.upsertJob(updatedJob);
        }

        await Promise.all(addOnAssignmentsPromises);

        setIsSaving(false);

        toasterStore.push(
          alert(
            t('dispatch.job.edit_rates_dialog.edit_successs_message'),
            AlertTypes.success,
          ),
        );
        onClose();
      } catch (error) {
        setIsSaving(false);

        connection.handleRequestError(
          error,
          t('error_messages.rates.failed_to_update_rates'),
        );
      }
    },
    [
      job?.id,
      job?.siteRateSchedule?.id,
      companyId,
      onClose,
      ordersDispatchStore,
      toasterStore,
    ],
  );

  const handleSubmit = useCallback(async () => {
    const haulerRateDetails = await new Promise<string | undefined>((resolve) => {
      if (hasHaulerRate) {
        haulerForm.handleSubmit((data) => {
          resolve(data.haulerRateType?.value);
        })();
      } else {
        resolve(undefined);
      }
    });

    const activeCustomerRef = customerJobRateInputRef.current;
    const activeAssigneeRef = vendorOrDriverJobRateInputRef.current;
    const hasInvalidCommission = [activeCustomerRef, activeAssigneeRef].some(
      (input) => input?.validateCommission?.() === false,
    );
    if (hasInvalidCommission) {
      return;
    }

    type SideResult = {
      data: RateDetailsDTO | undefined;
      addOnAssignments: AddOnAssignmentSchema[];
      addOnsDidChange: boolean;
    };

    const customerRateDetails = await new Promise<SideResult>((resolve) => {
      if (activeCustomerRef?.onSubmit) {
        activeCustomerRef.onSubmit((data, addOnAssignments, addOnsDidChange) => {
          resolve({ data, addOnAssignments, addOnsDidChange });
        });
      } else {
        resolve({ data: undefined, addOnAssignments: [], addOnsDidChange: false });
      }
    });

    const vendorRateDetails = await new Promise<SideResult>((resolve) => {
      if (isAssignedToVendor && activeAssigneeRef?.onSubmit) {
        activeAssigneeRef.onSubmit((data, addOnAssignments, addOnsDidChange) => {
          resolve({ data, addOnAssignments, addOnsDidChange });
        });
      } else {
        resolve({ data: undefined, addOnAssignments: [], addOnsDidChange: false });
      }
    });

    const driverRateDetails = await new Promise<SideResult>((resolve) => {
      if (isAssignedToDriver && activeAssigneeRef?.onSubmit) {
        activeAssigneeRef.onSubmit((data, addOnAssignments, addOnsDidChange) => {
          resolve({ data, addOnAssignments, addOnsDidChange });
        });
      } else if (canDefaultPayRateToDriver) {
        if (isDefaultingPayRateToDriver) {
          if (defaultVendorRateToDriverFeatureFlagEnabled) {
            resolve({
              data: deriveDriverRateFromVendorRate(vendorRateDetails.data),
              addOnAssignments: vendorRateDetails.addOnAssignments,
              addOnsDidChange: vendorRateDetails.addOnsDidChange,
            });
          } else {
            resolve({
              data: { targetRate: null, targetRateType: null, targetRateValue: null },
              addOnAssignments: [],
              addOnsDidChange: false,
            });
          }
        }
      } else {
        resolve({ data: undefined, addOnAssignments: [], addOnsDidChange: false });
      }
    });

    // Persist the schedule stamp only when the viewer owns the order.
    // `undefined` omits the field from the patch (parity with
    // `Job.deparseUpdate`). Sub-vendors and drivers down the dispatch chain
    // don't have access to the order's schedules — they must never write
    // `site_rate_schedule_id` (set or clear).
    const siteRateScheduleId = isOrderOwner ? (schedule?.id ?? null) : undefined;

    try {
      await updateJobRates({
        customerRate: customerRateDetails.data,
        driverRate: driverRateDetails.data,
        haulerRate: haulerRateDetails,
        vendorRate: vendorRateDetails.data,
        customerAddOnAssignments: customerRateDetails.addOnAssignments,
        vendorAddOnAssignments: vendorRateDetails.addOnAssignments,
        driverAddOnAssignments: driverRateDetails.addOnAssignments,
        customerAddOnsDidChange: customerRateDetails.addOnsDidChange,
        vendorAddOnsDidChange: vendorRateDetails.addOnsDidChange,
        driverAddOnsDidChange: driverRateDetails.addOnsDidChange,
        siteRateScheduleId,
      });
    } catch (error) {
      const commissionError = payCommissionFieldError(
        error,
        isAssignedToVendor ? 'vendor' : 'driver',
      );
      if (commissionError) {
        activeAssigneeRef?.setCommissionError?.(commissionError);
      }
    }
  }, [
    canDefaultPayRateToDriver,
    defaultVendorRateToDriverFeatureFlagEnabled,
    hasHaulerRate,
    haulerForm.handleSubmit,
    isAssignedToDriver,
    isAssignedToVendor,
    isDefaultingPayRateToDriver,
    isOrderOwner,
    schedule,
    updateJobRates,
  ]);

  const fetchParentAddOnAssignments =
    (rateOwnerType: RateOwnerType) => (signal?: AbortSignal) =>
      fetchParentAddOnAssignmentsForJob(companyId, rateOwnerType, job, signal);

  return (
    <Dialog
      isOpen={isOpen}
      onClose={isSaving ? undefined : onClose}
      preventCloseOnBackdropClick={isSaving}
      preventCloseOnEscapeKeyDown={isSaving}
    >
      <DialogHeader onClose={isSaving ? undefined : onClose}>
        <Box sx={{ alignItems: 'center', display: 'flex', flex: 1, gap: 1 }}>
          <OverflowAwareText sx={{ flex: 1, fontSize: '16px', fontWeight: 600 }}>
            {t('dispatch.job.edit_rates_dialog.title')}
          </OverflowAwareText>

          <PricingHistoryButton
            analyticsSurface="edit_rates_dialog"
            jobId={job?.id ?? null}
            subtitle={t('loads.pricing.history.job_ref', { ref: job?.jobId ?? '' })}
          />
        </Box>
      </DialogHeader>
      <DialogContent>
        {hasHaulerRate ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Typography variant="form-section-title">
              {t('order.form.rate_details')}
            </Typography>
            <AutocompleteFormField
              clearable
              control={haulerForm.control}
              disablePortal={false}
              errors={haulerForm.formState.errors}
              getLabel={(item) => item?.label ?? getRateTypeLabel(item)}
              getValue={(item) => item?.value ?? item}
              isCheckEnabled={false}
              isRequired={tomlinsonOrderModalFeatureFlagEnabled}
              label={`${t('form_fields.hauler_rate_type')}`}
              list={getHaulerRateTypeOptions()}
              name="haulerRateType"
              dataUi="page-dispatch__edit-job-rates__hauler-rate-type-input"
            />
          </Box>
        ) : (
          <JobFreightRateRow
            orderId={job?.order?.id ?? null}
            projectId={job?.project?.id ?? null}
            schedule={schedule}
            onSelectSchedule={applySchedule}
            onClearSchedule={clearSchedule}
            onUnlinkSchedule={unlinkSchedule}
            isAssignedToVendor={isAssignedToVendor}
            isAssignedToDriver={isAssignedToDriver}
            jobStartAt={job?.jobStartAt}
            customerRateDetailsRef={customerJobRateInputRef}
            vendorOrDriverRateDetailsRef={vendorOrDriverJobRateInputRef}
            disableCustomerRate={!isOrderOwner || !rateEditable.customer}
            disableAssigneeRate={!rateEditable.assignee}
            rateDisabledReasons={rateDisabledReasons}
            hasInitialCustomerRate={Boolean(initialRates.customer)}
            customerInitialRate={initialRates.customer}
            fetchCustomerRates={fetchCustomerRates}
            onCustomerRateFieldsChange={onCustomerRateFieldsChange}
            fetchCustomerParentAddOnAssignments={fetchParentAddOnAssignments(
              RateOwnerType.CUSTOMER,
            )}
            hasInitialVendorRate={Boolean(initialRates.vendor)}
            hasInitialDriverRate={Boolean(initialRates.driver)}
            vendorInitialRate={initialRates.vendor}
            driverInitialRate={initialRates.driver}
            fetchVendorRates={fetchVendorRates}
            fetchDriverRates={fetchDriverRates}
            onAssigneeRateFieldsChange={onAssigneeRateFieldsChange}
            fetchAssigneeParentAddOnAssignments={fetchParentAddOnAssignments(
              isAssignedToVendor ? RateOwnerType.VENDOR : RateOwnerType.DRIVER,
            )}
            analyticsSurface="edit_rates_dialog"
          />
        )}
      </DialogContent>
      <DialogActions>
        <SmallButton
          color="secondary"
          disabled={isSaving}
          onClick={onClose}
          variant="outlined"
        >
          {t('actions.cancel')}
        </SmallButton>

        <SmallLoadingButton
          loading={isSaving}
          onClick={handleSubmit}
          data-test-id="edit-job-rates-dialog__save"
        >
          {t('actions.save')}
        </SmallLoadingButton>
      </DialogActions>
    </Dialog>
  );
}
